import React, { useState, useEffect } from 'react'
import { getReviews } from '../services/reviewService'
import { transformReviews } from '../services/dataTransformer'

const Reviews = ({ reviews: initialReviews = [] }) => {
  const [reviews, setReviews] = useState(initialReviews || [])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState((initialReviews || []).length >= 6)
  const [totalCount, setTotalCount] = useState(0)
  const INITIAL_LOAD = 6
  
  // Update state when initialReviews changes
  useEffect(() => {
    setReviews(initialReviews || [])
    setHasMore((initialReviews || []).length >= INITIAL_LOAD)
  }, [initialReviews])

  const loadMoreReviews = async () => {
    setLoading(true)
    try {
      const { data, error, count } = await getReviews(6, reviews.length)
      
      if (error) {
        console.error('Error loading more reviews:', error)
        return
      }

      const transformedReviews = transformReviews(data || [])
      setReviews(prev => [...prev, ...transformedReviews])
      setTotalCount(count)
      
      // Check if there are more reviews to load
      setHasMore((reviews.length + transformedReviews.length) < count)
    } catch (error) {
      console.error('Error loading more reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  const showLessReviews = () => {
    // Reset to initial 6 reviews
    setReviews(initialReviews || [])
    setHasMore(totalCount > INITIAL_LOAD)
  }

  return (
    <section id="reviews" className="py-12 sm:py-16 lg:py-20 bg-white px-2 sm:px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            What Our Clients Say
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Read testimonials from our satisfied customers
          </p>
        </div>
        
        {reviews.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 mb-4">
              <i className="fas fa-star text-3xl text-yellow-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Reviews Yet</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Be the first to share your experience! Reviews will appear here once customers start sharing their feedback.
            </p>
          </div>
        ) : (
          <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-gray-50 rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition duration-300"
            >
              <div className="flex items-center mb-4">
                <img
                  src={review.image}
                  alt={review.name}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover mr-4"
                  loading="lazy"
                />
                    <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg">
                    {review.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {review.professionalName ? `Review for ${review.professionalName}` : review.service}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center mb-3">
                <div className="flex text-yellow-400 text-sm sm:text-base">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={i < review.rating ? 'fas fa-star' : 'far fa-star'}
                    ></i>
                  ))}
                </div>
                <span className="ml-2 text-xs sm:text-sm text-gray-500">
                  {review.date}
                </span>
              </div>
              
                  {review.comment && (
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                "{review.comment}"
              </p>
                  )}
            </div>
          ))}
        </div>

            <div className="text-center mt-8 sm:mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
              {hasMore && (
                <button
                  onClick={loadMoreReviews}
                  disabled={loading}
                  className="bg-secondary text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 text-base font-semibold"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      Show More Reviews
                    </>
                  )}
                </button>
              )}
              
              {!hasMore && reviews.length > INITIAL_LOAD && (
                <button
                  onClick={showLessReviews}
                  className="bg-gray-200 text-gray-800 px-8 py-3 rounded-lg hover:bg-gray-300 transition inline-flex items-center gap-2 text-base font-semibold"
                >
                  <i className="fas fa-chevron-up"></i>
                  Show Less
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default Reviews






